import React, { useState } from 'react';
import { Lightbulb, DollarSign, Clock, TrendingUp, AlertCircle } from 'lucide-react';

const AnalyticsInsights = ({ platform }) => {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getInsights = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = sessionStorage.getItem('token') || localStorage.getItem('token');
      
      const apiBaseUrl = process.env.REACT_APP_API_URL || 
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
          ? 'http://localhost:3003' 
          : 'https://cartresq-yiq2.onrender.com');
      
      const response = await fetch(`${apiBaseUrl}/api/ai/analytics-insights`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ platform })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data && Array.isArray(data.insights)) {
        setInsights(data.insights);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('AI insights error:', err);
      setError(err.message || 'Failed to get AI insights');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Lightbulb className="w-5 h-5 text-amber-500" />
          <h3 className="text-sm font-medium text-gray-900">AI Analytics Insights</h3>
        </div>
        <button
          onClick={getInsights}
          disabled={loading}
          className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Analyzing...' : 'Get Insights'}
        </button>
      </div>

      {error && (
        <div className="text-xs text-red-600 mt-2 p-2 bg-red-50 rounded">
          {error}
        </div>
      )}

      <div className="mt-3 space-y-3">
        {insights.length === 0 && !loading && (
          <p className="text-xs text-gray-500 italic">
            Click "Get Insights" to receive AI-powered recommendations based on your store's performance.
          </p>
        )}
        
        {insights.map((insight, idx) => {
          const priorityColor = insight.priority === 'high' ? 'red' : insight.priority === 'medium' ? 'yellow' : 'green';
          const priorityBg = insight.priority === 'high' ? 'bg-red-50 border-red-200' : insight.priority === 'medium' ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200';
          const priorityText = insight.priority === 'high' ? 'text-red-900' : insight.priority === 'medium' ? 'text-yellow-900' : 'text-green-900';
          const priorityDetail = insight.priority === 'high' ? 'text-red-800' : insight.priority === 'medium' ? 'text-yellow-800' : 'text-green-800';
          
          return (
            <div key={idx} className={`p-4 ${priorityBg} border rounded-lg shadow-sm`}>
              {/* Header with Priority Badge */}
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    {insight.priority === 'high' && (
                      <AlertCircle className="w-4 h-4 text-red-600" />
                    )}
                    <p className={`text-sm font-semibold ${priorityText}`}>
                      {insight.title}
                    </p>
                    {insight.priority && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        insight.priority === 'high' ? 'bg-red-200 text-red-800' :
                        insight.priority === 'medium' ? 'bg-yellow-200 text-yellow-800' :
                        'bg-green-200 text-green-800'
                      }`}>
                        {insight.priority.toUpperCase()} PRIORITY
                      </span>
                    )}
                    {insight.urgency && (
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-blue-200 text-blue-800">
                        {insight.urgency === 'immediate' ? '⚡ URGENT' : insight.urgency === 'this-week' ? '📅 THIS WEEK' : '📆 THIS MONTH'}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Detail */}
              <p className={`text-xs ${priorityDetail} mb-3 leading-relaxed`}>
                {insight.detail}
              </p>

              {/* Metrics Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                {insight.revenueImpact && (
                  <div className="flex items-center gap-1.5 p-2 bg-white rounded border border-gray-200">
                    <DollarSign className="w-3.5 h-3.5 text-green-600" />
                    <div>
                      <p className="text-xs text-gray-600">Revenue Impact</p>
                      <p className="text-xs font-bold text-green-700">{insight.revenueImpact}</p>
                    </div>
                  </div>
                )}
                {insight.timeToImplement && (
                  <div className="flex items-center gap-1.5 p-2 bg-white rounded border border-gray-200">
                    <Clock className="w-3.5 h-3.5 text-blue-600" />
                    <div>
                      <p className="text-xs text-gray-600">Time Needed</p>
                      <p className="text-xs font-bold text-blue-700">{insight.timeToImplement}</p>
                    </div>
                  </div>
                )}
                {insight.effort && (
                  <div className="flex items-center gap-1.5 p-2 bg-white rounded border border-gray-200">
                    <TrendingUp className="w-3.5 h-3.5 text-purple-600" />
                    <div>
                      <p className="text-xs text-gray-600">Effort</p>
                      <p className="text-xs font-bold text-purple-700 capitalize">{insight.effort}</p>
                    </div>
                  </div>
                )}
                {insight.cartresqFeature && (
                  <div className="flex items-center gap-1.5 p-2 bg-white rounded border border-gray-200">
                    <Lightbulb className="w-3.5 h-3.5 text-amber-600" />
                    <div>
                      <p className="text-xs text-gray-600">Feature</p>
                      <p className="text-xs font-bold text-amber-700 capitalize">{insight.cartresqFeature.replace('-', ' ')}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Specific Action Steps */}
              {insight.specificAction && (
                <div className="mt-3 p-2 bg-white rounded border border-gray-200">
                  <p className="text-xs font-semibold text-gray-700 mb-1.5">Action Steps:</p>
                  <div className="text-xs text-gray-600 whitespace-pre-line leading-relaxed">
                    {insight.specificAction}
                  </div>
                </div>
              )}

              {/* Expected Outcome */}
              {insight.expectedOutcome && (
                <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                  <p className="text-xs font-semibold text-blue-900 mb-1">Expected Outcome:</p>
                  <p className="text-xs text-blue-800">{insight.expectedOutcome}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AnalyticsInsights;

